import React from 'react';
import { useSelector } from 'react-redux';

import {
  handleChange,
  isRequired,
  getOptions,
  getFieldKey,
  fieldShouldDisplay,
} from '../../../../utils/form';

import FormLabel from './FormLabel';
import { Collapse } from '../../../../common/components';
import { evaluateConditions } from '../../../../common/utils/enrollment';

function Radio({
  boldLabel,
  children,
  disabled,
  name,
  options = [], // this prop will be overidden if options are specified in form JSON config
  unsetFieldsOnCondition = {},
  // unsetFieldsOnCondition takes an object in the shape of
  // {
  //   condition: (some boolean),
  //   fieldsToUnset: [ 'field1', 'field2', ... ],\
  // }
  onChangeCallback = () => {}, // This callback is passed the new value of the field as it's 1st arg
}) {
// This component requires an options array to be provided in the shape
// [ { value: 'val', label: 'whatever text' }, &etc... ]

  const {
    values,
    form: { form },
  } = useSelector(store => store);
  const value = values[name];
  const required = isRequired({ form, name, currentValues: values });
  const validationRegex = useSelector(store => store.validation[name]);
  const defaultLabel = useSelector(store => store.labels[name]);
  const { submittedFlag } = useSelector(store => store.enrollment);
  const customLabel = getFieldKey(form, name, 'label');

  // defaults to what is being passed in by prop `options`,
  //  can be overidden by setting options in form JSON config
  let opts;
  const configOpts = getOptions(form, name) || [];
  if (configOpts.length) {
    opts = configOpts;
  } else {
    opts = options;
  }

  // if field doesn't exist or is conditionally hidden, don't show it.
  if (!fieldShouldDisplay(form, name, values)) return null;

  function evalConditions(conditions = [], defaultVal = false) {
    let rval = defaultVal;
    if (conditions.length) rval = evaluateConditions({ conditions, currentValues: values });
    return rval;
  }

  return (
    <>
      <FormLabel label={customLabel || defaultLabel} name={name} required={required} />
      {opts.map(option => {
        const conditionallyDisabled = evalConditions(option.disableIf, false);
        const disabledMessage = conditionallyDisabled ? option.disabledMessage : null;
        const displayIf = evalConditions(option.displayIf, true);

        return displayIf ? (
          <div key={option.value}>
            <div
              role="button"
              tabIndex={0}
              className="form-check my-1"
              style={{ paddingLeft: '1.75rem' }}
              onClick={(!conditionallyDisabled && !submittedFlag && !option.disabled)
                ? () => handleChange({
                  value: option.value,
                  name,
                  validationRegex,
                  forceValidation: true,
                  unsetFieldsOnCondition,
                  onChangeCallback,
                  required,
                })
                : undefined}
            >
              <input
                className={['form-check-input'].join(' ')}
                type="radio"
                id={String(option.value)}
                name={name}
                value={option.value}
                checked={option.value === value}
                disabled={disabled || option.disabled || submittedFlag || conditionallyDisabled}
                onChange={() => {}} // React throws an error if I don't put a blank onChange here
              />
              <label
                htmlFor={String(option.value)}
                className={['form-check-label', ((option.desc || boldLabel) && 'fw-bold')].join(' ')}
              >
                {option.label}
                <span className="text-danger ms-3">{disabledMessage}</span>
              </label>
              {option.desc && (
                <div className="text-muted">
                  {option.desc}
                </div>
              )}
            </div>
            <Collapse recalculateHeightOnChange={value} isOpen={(option.value === value)} indent="2">
              {children}
            </Collapse>
          </div>
        ) : null;
      })}
    </>
  );
}

export default Radio;
